import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth';
import { currentSessionScope } from '../services/session-scope';
import { NotificationService } from '../services/notification';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const notificationService = inject(NotificationService);
  const messageService = inject(MessageService);

  const scope = currentSessionScope();
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (scope !== currentSessionScope()) return throwError(() => error);
      let summary = 'Erreur';
      let errorMessage = 'Une erreur inconnue est survenue';

      if (error.error instanceof ErrorEvent) {
        // Erreur côté client
        errorMessage = `Erreur: ${error.error.message}`;
      } else {
        // Erreur côté serveur — extraire le message (string ou tableau de validation)
        const rawMessage = error.error?.message;
        errorMessage = Array.isArray(rawMessage)
          ? rawMessage.join(', ')
          : rawMessage || `Code d'erreur: ${error.status} — ${error.message}`;

        // 401 hors endpoint de login → session expirée
        const isLoginRequest = /\/auth\/(login|register|refresh|logout)(\?.*)?$/.test(req.url);
        if (error.status === 401 && !isLoginRequest) {
          authService.logout();
          router.navigate(['/auth/login']);
          summary = 'Session expirée';
          errorMessage = 'Veuillez vous reconnecter.';
        } else if (error.status === 403) {
          summary = 'Accès refusé';
          if (!rawMessage) {
            errorMessage = 'Vous n\'avez pas les droits nécessaires pour cette action.';
          }
        } else if (error.status === 0) {
          summary = 'Connexion impossible';
          errorMessage = 'Le serveur ne répond pas. Vérifiez votre connexion.';
        } else if (error.status >= 500) {
          summary = `Erreur serveur (${error.status})`;
        } else if (error.status >= 400) {
          summary = `Requête invalide (${error.status})`;
        }
      }

      // Toast global uniquement pour les erreurs "système" (network, auth, 5xx).
      // Les 4xx (validation, conflits, not-found) restent gérées localement
      // par les composants qui affichent un message spécifique au formulaire.
      const status = error.status;
      const shouldToast =
        status === 0 || status === 401 || status === 403 || status >= 500;
      if (shouldToast) {
        messageService.add({
          severity: 'error',
          summary,
          detail: errorMessage,
          life: 6000,
        });
      }

      notificationService.addNotification({
        title: summary,
        message: errorMessage,
        type: 'error'
      });

      return throwError(() => error);
    })
  );
};
